import glfx from 'glfx';
import { cloneDeep, flatten } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { BaseOperateView, IFramework } from '@/framework';
import { getLocaleStore } from '@/i18n';
import { loadImageAsync } from '@/utils/file-kit';
import { calculateBoundingBox } from '@/utils/math-kit';
import { ILayerProps, LayerType } from '@/version-types';
import { useDeformationImgState } from '../states/deformation-img';
import { CornerPoint, EDeformationEvent, RectangleAnchorPointName } from '../types';
import DeformationImgOperate from './deformation-img-operate';
import RenderStage, { GLFX_CANVAS_ID, StageWidthScale } from './render-stage';

const getInitPoints = (layer: ILayerProps | null) => {
    if (!layer) return [];
    const { width, height } = layer;
    return [
        {
            left: (width * StageWidthScale - width) / 2,
            top: (height * StageWidthScale - height) / 2,
            type: RectangleAnchorPointName.LEFT_TOP,
        },
        {
            left: (width * StageWidthScale - width) / 2 + width,
            top: (height * StageWidthScale - height) / 2,
            type: RectangleAnchorPointName.RIGHT_TOP,
        },
        {
            left: (width * StageWidthScale - width) / 2 + width,
            top: (height * StageWidthScale - height) / 2 + height,
            type: RectangleAnchorPointName.RIGHT_BOTTOM,
        },
        {
            left: (width * StageWidthScale - width) / 2,
            top: (height * StageWidthScale - height) / 2 + height,
            type: RectangleAnchorPointName.LEFT_BOTTOM,
        },
    ];
};

const LayerPosition = { x: 0, y: 0 };
const StagePosition = { x: 0, y: 0 };
const DeformationImgWrap: React.FC<{
    scale: number;
    framework: IFramework;
}> = ({ scale, framework }) => {
    const [activeLayer, setActiveLayer] = useState<ILayerProps | null>(
        framework.operate.getActivePickLayer() || framework.operate.getActiveLayer(),
    );

    const layerId = activeLayer?.id;
    const [originLayer] = useState(cloneDeep(activeLayer));
    const [latestPoints, setLatestPoints] = useState<CornerPoint[]>(getInitPoints(activeLayer));
    const [startPoints] = useState<CornerPoint[]>(getInitPoints(activeLayer));

    const glfxCanvas = useRef<typeof glfx>(null);
    const texture = useRef<typeof glfx.texture>(null);
    const layerImage = useDeformationImgState()((state) => state.layerImage);

    useEffect(() => {
        if (!activeLayer) return;
        const getImageCanvas = async () => {
            const stage = document.querySelector(`#${GLFX_CANVAS_ID}`) as HTMLCanvasElement;
            if (!stage) return;
            if (!layerImage) return;

            const imgCanvas = document.createElement('canvas');
            imgCanvas.width = activeLayer.width * StageWidthScale;
            imgCanvas.height = activeLayer.height * StageWidthScale;
            imgCanvas
                .getContext('2d')
                ?.drawImage(
                    layerImage,
                    0,
                    0,
                    layerImage.width,
                    layerImage.height,
                    (imgCanvas.width - activeLayer.width) / 2,
                    (imgCanvas.height - activeLayer.height) / 2,
                    activeLayer.width,
                    activeLayer.height,
                );

            const canvas = glfx.canvas();
            canvas.className = 'deformation-img-canvas';
            glfxCanvas.current = canvas;
            texture.current = canvas.texture(imgCanvas);

            canvas.draw(texture.current).update();
            stage.width = activeLayer.width * StageWidthScale;
            stage.height = activeLayer.height * StageWidthScale;
            stage.replaceWith(canvas);
        };

        const stagePosition = document.getElementById('deformation-img-plugin-wrap')?.getBoundingClientRect();
        if (stagePosition) {
            StagePosition.x = stagePosition.left;
            StagePosition.y = stagePosition.top;
        }
        const layerPosition = document.getElementById('deformation-img-render-stage')?.getBoundingClientRect();
        if (layerPosition) {
            LayerPosition.x = layerPosition.left;
            LayerPosition.y = layerPosition.top;
        }
        getImageCanvas();
    }, []);

    useEffect(
        () => () => {
            glfxCanvas.current?.destroy?.();
            texture.current?.destroy?.();
        },
        [],
    );

    useEffect(() => {
        if (latestPoints.length <= 0) return;
        if (startPoints.length <= 0) return;
        const formatePoints = (points: CornerPoint[]) => {
            return flatten(
                points.map((point) => {
                    return [point.left, point.top];
                }),
            );
        };
        glfxCanvas.current
            ?.draw(texture.current!)
            .perspective(formatePoints(startPoints), formatePoints(latestPoints))
            .update();
    }, [latestPoints, startPoints]);

    useEffect(() => {
        if (!layerId) return;
        const handler = framework.events.onLayerChange(() => {
            setActiveLayer(framework.operate.getActivePickLayer() || framework.operate.getActiveLayer());
        }, layerId);

        return () => {
            framework.events.off(handler);
        };
    }, [layerId]);

    const getCanvasBaseURL = async () => {
        const fxCanvas = glfxCanvas.current;
        fxCanvas.update();

        const dataURL = fxCanvas.toDataURL();
        const image = await loadImageAsync(dataURL);

        const canvas = document.createElement('canvas');

        const ctx = canvas.getContext('2d');

        const points = latestPoints.map((point) => {
            return [point.left, point.top];
        });

        const { minX, minY, maxX, maxY } = calculateBoundingBox(points);

        canvas.width = maxX - minX;
        canvas.height = maxY - minY;

        ctx?.drawImage(image, minX, minY, canvas.width, canvas.height, 0, 0, canvas.width, canvas.height);

        return canvas;
    };

    useEffect(() => {
        const handle = async () => {
            const canvas = await getCanvasBaseURL();
            framework.events.emit(EDeformationEvent.CONFIRM, canvas);
        };
        const id = framework.events.on(EDeformationEvent.INNER_CONFIRM, handle);
        return () => {
            framework.events.off(id);
        };
    }, [latestPoints]);

    // 防止闪一下
    if (!activeLayer || activeLayer?.layerType !== LayerType.IMAGE_LAYER || !originLayer) {
        return null;
    }

    return (
        <BaseOperateView layer={{ ...activeLayer, id: `${activeLayer.id}-crop-img` }}>
            <RenderStage
                scale={scale}
                activeLayer={activeLayer}
                framework={framework}
                latestPoints={latestPoints}
                startPoints={startPoints}
            />
            <DeformationImgOperate
                scale={scale}
                framework={framework}
                layer={activeLayer}
                latestPoints={latestPoints}
                setLatestPoints={setLatestPoints}
                maxWidth={activeLayer.width * StageWidthScale}
                maxHeight={activeLayer.height * StageWidthScale}
            />
        </BaseOperateView>
    );
};

export default DeformationImgWrap;
